/**
 * Client for the IV / realized-vol / GARCH-HAR / net-GEX indicator endpoint
 * (`/api/iv-tracking/indicator`). This is the REAL backend data that replaces the
 * old client-side fake realized vol (atmIV*0.85) and fake DTE-bucket "HAR".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiUrl } from '../config/api';
import { getMmecAuthHeaderFields } from '../utils/authUtils';

export interface IvRealizedVol {
  hv10: number | null;
  hv20: number | null;
  hv30: number | null;
  intraday: number | null;
}

export interface IvForecast {
  ok: boolean;
  rv_now_annualized?: number;
  forecast_1d?: number;
  forecast_5d?: number;
  forecast_22d?: number;
  band_low_1d?: number;
  band_high_1d?: number;
  spike_probability?: number;
  spike_threshold_annualized?: number;
  live_breach?: boolean;
  vol_of_vol?: number;
  n_obs?: number;
}

export interface GexProfilePoint {
  strike: number;
  call_gex: number;
  put_gex: number;
  net_gex: number;
}

export interface IvGex {
  ok: boolean;
  spot?: number;
  net_gex?: number;
  net_gex_bn?: number;
  call_gamma_notional?: number;
  put_gamma_notional?: number;
  gamma_flip?: number | null;
  call_wall?: number | null;
  put_wall?: number | null;
  regime?: 'positive' | 'negative';
  profile?: GexProfilePoint[];
  strikes?: number;
}

export interface SmilePoint {
  strike: number;
  moneyness: number;
  iv: number;
  call_iv: number | null;
  put_iv: number | null;
}

export interface IvSmile {
  expiry: string;
  dte: number;
  skew_5pct: number | null;
  points: SmilePoint[];
}

export interface IvIndicator {
  ok: boolean;
  symbol: string;
  asof: string;
  source?: string;
  underlying: number | null;
  implied_volatility: number | null;
  iv_source?: 'live_chain' | 'stored';
  iv_rank: number | null;
  iv_percentile: number | null;
  realized_vol: IvRealizedVol;
  iv_rv_gap: IvRealizedVol;
  forecast: IvForecast;
  gex: IvGex;
  smile: IvSmile | null;
  flat?: Record<string, number | string | null>;
  error?: string;
}

export async function fetchIvIndicator(symbol: string, signal?: AbortSignal): Promise<IvIndicator | null> {
  const sym = (symbol || '').trim().toUpperCase();
  if (!sym) return null;
  const u = new URL(getApiUrl('/api/iv-tracking/indicator'), window.location.origin);
  u.searchParams.set('symbol', sym);
  const res = await fetch(u.toString(), {
    signal,
    credentials: 'include',
    headers: { ...getMmecAuthHeaderFields() },
  });
  if (!res.ok) throw new Error(`iv-tracking indicator HTTP ${res.status}`);
  return (await res.json()) as IvIndicator;
}

const POLL_MS = 30_000;

export interface UseIvIndicator {
  data: IvIndicator | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/** Poll the indicator for a symbol; refetches on symbol change and every 30s. */
export function useIvIndicator(symbol: string | null | undefined): UseIvIndicator {
  const [data, setData] = useState<IvIndicator | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const acRef = useRef<AbortController | null>(null);
  const symRef = useRef<string>('');

  const sym = (symbol || '').trim().toUpperCase();
  symRef.current = sym;

  const refresh = useCallback(() => {
    const target = symRef.current;
    if (!target) {
      setData(null);
      return;
    }
    acRef.current?.abort();
    const ac = new AbortController();
    acRef.current = ac;
    setLoading(true);
    fetchIvIndicator(target, ac.signal)
      .then(d => {
        if (ac.signal.aborted) return;
        setData(d);
        setError(d && d.ok === false ? d.error || 'unavailable' : null);
      })
      .catch(e => {
        if (ac.signal.aborted) return;
        setError(String(e?.message || e));
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, POLL_MS);
    return () => {
      window.clearInterval(id);
      acRef.current?.abort();
    };
  }, [sym, refresh]);

  return { data, loading, error, refresh };
}
